/**
 * MoonScheduledStepView
 *
 * Scheduled procedure steps use the same appointment row as the rest of the
 * Care surfaces. The procedure-specific guidance remains in the educational
 * cards below the row instead of being duplicated here.
 */

import { CalendarDays } from "lucide-react-native";
import { useLocale } from "@src/packages/i18n/useLocale";
import { formatDate } from "@src/packages/i18n/formatters";
import { formatPhaseTime } from "@src/packages/cycles/presenters/formatPhaseDate";
import type { TreatmentStepConfig } from "@src/packages/homepage/sections/TreatmentProgressModal";
import AppointmentRow from "../../rows/AppointmentRow";
import type { AppointmentScheduleItem } from "../../schedule.types";

function parseIsoDate(iso: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function isDue(iso: string, now: Date): boolean {
  const target = parseIsoDate(iso);
  if (!target) return false;

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return target.getTime() <= today.getTime();
}

export interface MoonScheduledStepViewProps {
  step: TreatmentStepConfig;
  scheduledFor: string;
  scheduledTime?: string | null;
  onEditSchedule: () => void;
  onLogResults: () => void;
}

export function MoonScheduledStepView({
  step,
  scheduledFor,
  scheduledTime,
  onEditSchedule,
  onLogResults,
}: MoonScheduledStepViewProps) {
  const { locale } = useLocale();
  const timeLabel = scheduledTime
    ? formatPhaseTime(scheduledTime, locale)
    : null;
  const due = isDue(scheduledFor, new Date());

  const appointment: AppointmentScheduleItem = {
    type: "appointment",
    id: step.id,
    title: step.title,
    subtitle: timeLabel ?? step.description,
    time: timeLabel ?? undefined,
    icon: CalendarDays,
  };

  return (
    <AppointmentRow
      item={appointment}
      surface="glass"
      dateLabel={formatDate(scheduledFor, locale, {
        month: "short",
        day: "numeric",
      })}
      onPress={due ? onLogResults : onEditSchedule}
    />
  );
}
